import { useEffect, useState } from 'react';
import { useT } from '@/shared/lib/i18n';
import { Button, PhaseHeader, Pin } from '@/shared/ui';

export type DiscussTimerProps = {
  durationSeconds: number;
  /** Fired when timer hits 0 — orchestrator advances to elimination picker. */
  onElapsed: () => void;
  /** Host can short-circuit timer. Same destination as onElapsed. */
  onSkip: () => void;
  /** Living suspect count for the pill below the timer. Optional — pill hides at 0. */
  suspectsAlive?: number;
};

const DANGER_THRESHOLD = 10;
const RING_RADIUS = 100;
const RING_CIRC = 2 * Math.PI * RING_RADIUS; // ≈ 628

const fmt = (s: number): string => {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};

/**
 * Open-table debate countdown — pinned phase header sheet plus a
 * cream paper-sheet timer card holding the SVG ring + remaining
 * `mm:ss`. At ≤10s the ring stroke + sub-line shift to red. At 0,
 * fires `onElapsed`. Host can also short-circuit via the bottom CTA.
 */
export const DiscussTimer = ({
  durationSeconds,
  onElapsed,
  onSkip,
  suspectsAlive,
}: DiscussTimerProps) => {
  const t = useT();
  const [remaining, setRemaining] = useState(durationSeconds);

  useEffect(() => {
    if (remaining <= 0) {
      onElapsed();
      return;
    }
    const id = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(id);
  }, [remaining, onElapsed]);

  const danger = remaining <= DANGER_THRESHOLD;
  const pct = durationSeconds === 0 ? 0 : remaining / durationSeconds;
  const dasharray = `${pct * RING_CIRC} ${RING_CIRC}`;
  const stroke = danger ? 'var(--color-und)' : 'var(--color-orange-mark)';

  return (
    <main className="mx-auto flex h-full w-full max-w-md flex-col">
      <PhaseHeader
        eyebrow={t('round.discuss.phase_eyebrow')}
        title={t('round.discuss.title')}
        descriptor={t('round.discuss.descriptor')}
      />

      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-5 pt-6 pb-2">
        <div className="relative w-full max-w-[340px]">
          <div
            className="paper-dossier relative px-6 pt-5 pb-6"
            style={{ transform: 'rotate(-0.6deg)' }}
          >
            <Pin left={28} top={-6} />
            <Pin left={'calc(100% - 28px)'} top={-6} />

            <div className="type text-center text-[10px] text-ink/55 uppercase tracking-[0.22em]">
              {t('round.discuss.timer_eyebrow')}
            </div>

            <div className="relative mx-auto mt-3 h-[220px] w-[220px]">
              <svg
                viewBox="0 0 220 220"
                className="absolute inset-0"
                style={{ transform: 'rotate(-90deg)' }}
                role="img"
                aria-hidden
              >
                <title>countdown</title>
                <circle
                  cx="110"
                  cy="110"
                  r={RING_RADIUS}
                  fill="none"
                  stroke="rgba(26,26,26,.08)"
                  strokeWidth="10"
                />
                <circle
                  cx="110"
                  cy="110"
                  r={RING_RADIUS}
                  fill="none"
                  stroke={stroke}
                  strokeWidth="10"
                  strokeDasharray={dasharray}
                  strokeLinecap="round"
                  style={{
                    transition: 'stroke-dasharray 1s linear, stroke 200ms ease',
                  }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div
                  className="font-display font-extrabold text-[64px] leading-none tracking-[-0.04em] tabular-nums"
                  style={{ color: 'var(--color-ink)' }}
                >
                  {fmt(remaining)}
                </div>
                <div
                  className="mt-1.5 text-[12px] italic"
                  style={{ color: danger ? 'var(--color-und)' : 'var(--color-stamp-red)' }}
                >
                  {t(danger ? 'round.discuss.timer_danger_sub' : 'round.discuss.timer_calm_sub')}
                </div>
              </div>
            </div>

            {suspectsAlive !== undefined && suspectsAlive > 0 && (
              <div className="mt-4 flex justify-center">
                <span
                  className="inline-flex items-center gap-2 rounded-full border border-ink/30 bg-cream/60 px-3 py-1 type text-[11px] text-ink uppercase tracking-[0.18em]"
                  style={{ boxShadow: 'var(--shadow-stamp-xs)' }}
                >
                  <span
                    aria-hidden
                    className="inline-block h-1.5 w-1.5 rounded-full"
                    style={{ background: 'var(--color-und)' }}
                  />
                  {t('round.discuss.suspects_pill', { count: suspectsAlive })}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-5 pt-2 pb-5">
        <Button variant="orange" onClick={onSkip}>
          {t('online.discuss.host_open_vote_button')}
        </Button>
      </div>
    </main>
  );
};
